import * as React from "react";
import {
	Box,
	Card,
	Collapse,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	OutlinedInput,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
	useColorScheme,
} from "@mui/material";
import {
	ArrowsOutSimple,
	CaretDownIcon,
	CaretRightIcon,
	Code,
	CopyIcon,
	CopySimple,
	Plus,
	X,
} from "@phosphor-icons/react";

import { ConversationTemplate, useConversationTemplates } from "@/hooks/api/use-conversation-templates";
import { toast } from "@/components/core/toaster";

export function TextAreaWithExpand({
	field,
	label,
	withCustomFields = false,
	placeholder,
	summaryInfo,
	withFormatJson = false,
}: {
	field: any;
	label: string;
	withCustomFields?: boolean;
	placeholder?: string;
	summaryInfo?: any;
	withFormatJson?: boolean;
}) {
	const [isExpanded, setIsExpanded] = React.useState(false);
	const [isCustomFieldsExpanded, setIsCustomFieldsExpanded] = React.useState(false);
	const [isDialogFieldsExpanded, setIsDialogFieldsExpanded] = React.useState(false);
	const [isSummaryOpen, setIsSummaryOpen] = React.useState(false);

	const { colorScheme } = useColorScheme();

	const textareaRef = React.useRef<HTMLTextAreaElement>(null);
	const dialogTextareaRef = React.useRef<HTMLTextAreaElement>(null);

	const {
		data: conversationTemplates = [],
		isFetching: _isFieldsFetching,
		isPending: _isFieldsPending,
		isLoading: _isFieldsLoading,
	} = useConversationTemplates();

	const handleInsertField = React.useCallback(
		(fieldName: string, useDialogRef = false) => {
			const token = fieldName;
			const textarea = useDialogRef ? dialogTextareaRef.current : textareaRef.current;

			if (textarea) {
				const start = textarea.selectionStart || 0;
				const end = textarea.selectionEnd || 0;
				const scrollTop = textarea.scrollTop || 0;
				const currentValue = field.value || "";
				const newValue = currentValue.slice(0, start) + token + currentValue.slice(end);

				field.onChange({ target: { value: newValue } });

				requestAnimationFrame(() => {
					setTimeout(() => {
						textarea.focus();
						const newCursorPos = start + token.length;
						textarea.setSelectionRange(newCursorPos, newCursorPos);
						textarea.scrollTop = scrollTop;
					}, 0);
				});

				toast.success("Field inserted");
			} else {
				navigator.clipboard.writeText(token);
				toast.success("Copied to clipboard");
			}
		},
		[field]
	);

	const handleExpand = React.useCallback(() => {
		setIsExpanded(true);
		setTimeout(() => {
			const input = dialogTextareaRef.current;
			if (input) {
				input.focus();

				const length = input.value?.length || 0;
				input.setSelectionRange(length, length);
			}
		}, 0);
	}, []);

	const handleClose = React.useCallback(() => {
		setIsExpanded(false);
	}, []);

	const handleFormatJson = React.useCallback(() => {
		const currentValue = field.value || "";
		
    if (!currentValue.trim()) {
			return;
		}

		const parsed = JSON.parse(currentValue);

		const formatted = JSON.stringify(parsed, null, 2);
		field.onChange({ target: { value: formatted } });
	}, [field]);

	return (
		<>
			<Box>
				<Box sx={{ position: "relative", width: "100%", mt: "8px" }}>
					<OutlinedInput
						{...field}
						value={field.value || ""}
						placeholder={placeholder || `Enter ${label}`}
						multiline
						minRows={4}
						maxRows={8}
						sx={{
							width: "100%",
							pr: 5,
							...((withCustomFields || summaryInfo) && {
								borderBottomLeftRadius: 0,
								borderBottomRightRadius: 0,
								borderBottom: "none",
							}),
						}}
						inputRef={textareaRef}
					/>
					<Stack
						direction="column-reverse"
						spacing={0.5}
						sx={{
							position: "absolute",
							top: 8,
							right: 8,
						}}
					>
						{withFormatJson && (
							<Tooltip title="Format JSON">
								<IconButton
									onClick={handleFormatJson}
									sx={{
										p: 0.5,
										"&:hover": {
											backgroundColor: "action.hover",
										},
									}}
									size="small"
								>
									<Code size={16} />
								</IconButton>
							</Tooltip>
						)}
						<IconButton
							onClick={handleExpand}
							sx={{
								p: 0.5,
								"&:hover": {
									backgroundColor: "action.hover",
								},
							}}
							size="small"
						>
							<ArrowsOutSimple size={16} />
						</IconButton>
					</Stack>
				</Box>

				{withCustomFields && (
					<Box
						sx={{
							mt: 0,
							border: "1px solid var(--mui-palette-divider)",

							overflow: "hidden",
							borderRadius: 0,
							borderBottomLeftRadius: "12px",
							borderBottomRightRadius: "12px",
						}}
					>
						<Stack
							direction="row"
							spacing={1}
							alignItems="center"
							justifyContent="space-between"
							sx={{
								px: 2,
								py: 1,
								cursor: "pointer",
								borderBottom: isCustomFieldsExpanded ? "1px solid var(--mui-palette-divider)" : "none",
							}}
							onClick={() => setIsCustomFieldsExpanded(!isCustomFieldsExpanded)}
						>
							<Typography variant="subtitle2">Variables</Typography>
							<IconButton size="small" sx={{ p: 0.5 }}>
								{isCustomFieldsExpanded ? <CaretDownIcon size={16} /> : <CaretRightIcon size={16} />}
							</IconButton>
						</Stack>
						<Collapse in={isCustomFieldsExpanded} timeout={300}>
							<Box sx={{ maxHeight: 300, overflowY: "auto" }}>
								<Table size="small" stickyHeader>
									<TableHead>
										<TableRow>
											<TableCell sx={{ py: 0.5 }}>
												<Typography variant="overline" color="text.secondary">
													Name
												</Typography>
											</TableCell>
											<TableCell sx={{ py: 0.5 }}>
												<Typography variant="overline" color="text.secondary">
													Description
												</Typography>
											</TableCell>
											<TableCell align="right" sx={{ py: 0.5 }}>
												<Typography variant="overline" color="text.secondary">
													Action
												</Typography>
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{conversationTemplates.map((f: ConversationTemplate) => (
											<TableRow key={f.name}>
												<TableCell sx={{ py: 0.5 }}>
													<Typography variant="body2" sx={{ fontFamily: "monospace" }}>
														{f.name}
													</Typography>
												</TableCell>
												<TableCell sx={{ py: 0.5 }}>
													<Typography variant="body2" color="text.secondary">
														{f.description || ""}
													</Typography>
												</TableCell>
												<TableCell align="right" sx={{ py: 0.5 }}>
													<Stack direction="row" spacing={0.5} justifyContent="flex-end">
														<Tooltip title="Insert at cursor position">
															<IconButton
																size="small"
																onClick={(e) => {
																	e.stopPropagation();
																	handleInsertField(f.name);
																}}
															>
																<Plus size={16} />
															</IconButton>
														</Tooltip>
														<Tooltip title="Copy token">
															<IconButton
																size="small"
																onClick={(e) => {
																	e.stopPropagation();
																	navigator.clipboard.writeText(f.name);
																	toast.success("Copied to clipboard");
																}}
															>
																<CopySimple size={16} />
															</IconButton>
														</Tooltip>
													</Stack>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</Box>
						</Collapse>
					</Box>
				)}

				{summaryInfo && (
					<Box
						sx={{
							mt: 0,
							border: "1px solid var(--mui-palette-divider)",
							overflow: "hidden",
							borderRadius: 0,
							borderBottomLeftRadius: "12px",
							borderBottomRightRadius: "12px",
						}}
					>
						<Stack
							direction="row"
							spacing={1}
							alignItems="center"
							justifyContent="space-between"
							sx={{
								px: 2,
								py: 1,
								cursor: "pointer",
							}}
							onClick={() => setIsSummaryOpen(!isSummaryOpen)}
						>
							<Stack
								direction={{ xs: "column", md: "row" }}
								spacing={1}
								alignItems={{ xs: "flex-start", md: "center" }}
							>
								<Typography variant="subtitle2">Summary Schema</Typography>
								<Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
									(No need to copy — it will be added to the prompt automatically.)
								</Typography>
							</Stack>
							<Stack direction="row" spacing={1} alignItems="center">
								<Tooltip title={"Copy to clipboard"}>
									<IconButton
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											navigator.clipboard.writeText(JSON.stringify(summaryInfo, null, 2));
											toast.success("Copied to clipboard");
										}}
										sx={{ p: 0.5 }}
									>
										<CopyIcon size={16} />
									</IconButton>
								</Tooltip>
								<Tooltip title={isSummaryOpen ? "Collapse" : "Expand"}>
									<IconButton size="small" onClick={() => setIsSummaryOpen(!isSummaryOpen)} sx={{ p: 0.5 }}>
										{isSummaryOpen ? <CaretDownIcon size={16} /> : <CaretRightIcon size={16} />}
									</IconButton>
								</Tooltip>
							</Stack>
						</Stack>
						<Collapse in={isSummaryOpen} timeout={600}>
							<Card
								sx={{
									m: 2,
									mt: 0,
									p: 2,
									border: "1px solid var(--mui-palette-divider)",
									boxShadow: "none !important",
									backgroundColor:
										colorScheme === "dark" ? "var(--mui-palette-Skeleton-bg)" : "var(--mui-palette-grey-100)",
								}}
							>
								<Typography variant="body1" sx={{ whiteSpace: "pre-wrap", fontSize: 14 }}>
									{JSON.stringify(summaryInfo, null, 2)}
								</Typography>
							</Card>
						</Collapse>
					</Box>
				)}
			</Box>

			<Dialog
				open={isExpanded}
				onClose={handleClose}
				maxWidth="md"
				fullWidth
				fullScreen
				PaperProps={{
					sx: {
						m: 0,
						height: "100vh",
						maxHeight: "100vh",
						display: "flex",
						flexDirection: "column",
					},
				}}
			>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					{label}
					<Stack direction="row" spacing={0.5} alignItems="center">
						{withFormatJson && (
							<Tooltip title="Format JSON">
								<IconButton onClick={handleFormatJson} size="small">
									<Code size={20} />
								</IconButton>
							</Tooltip>
						)}
						<IconButton onClick={handleClose} size="small">
							<X size={20} />
						</IconButton>
					</Stack>
				</DialogTitle>
				<DialogContent
					sx={{
						flex: 1,
						p: 0,
						pt: "0px !important",
						display: "flex",
						flexDirection: "column",
						overflow: "hidden",
					}}
				>
					<Box
						sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", p: 2, pt: "2px !important" }}
					>
						<OutlinedInput
							{...field}
							value={field.value || ""}
							multiline
							fullWidth
							placeholder={placeholder || `Enter ${label}`}
							variant="outlined"
							inputRef={dialogTextareaRef}
							sx={{
								flex: 1,
								"& textarea": {
									height: "100% !important",
									overflowY: "auto !important",
								},
								...(withCustomFields && {
									borderBottomLeftRadius: 0,
									borderBottomRightRadius: 0,
									borderBottom: "none",
								}),
							}}
						/>
					</Box>
					{withCustomFields && (
						<Box
							sx={{
								border: "1px solid var(--mui-palette-divider)",
								overflow: "hidden",
								borderRadius: 1,
								borderTopLeftRadius: 0,
								borderTopRightRadius: 0,
								maxHeight: "50%",
								display: "flex",
								flexDirection: "column",
								flexShrink: 0,
								m: 2,
								mt: -2,
							}}
						>
							<Stack
								direction="row"
								spacing={1}
								alignItems="center"
								justifyContent="space-between"
								sx={{
									px: 2,
									py: 1,
									cursor: "pointer",
									borderBottom: isDialogFieldsExpanded ? "1px solid var(--mui-palette-divider)" : "none",
								}}
								onClick={() => setIsDialogFieldsExpanded(!isDialogFieldsExpanded)}
							>
								<Typography variant="subtitle2">Variables</Typography>
								<IconButton size="small" sx={{ p: 0.5 }}>
									{isDialogFieldsExpanded ? <CaretDownIcon size={16} /> : <CaretRightIcon size={16} />}
								</IconButton>
							</Stack>
							<Collapse in={isDialogFieldsExpanded} timeout={300}>
								<Box sx={{ maxHeight: 300, overflowY: "auto" }}>
									<Table size="small" stickyHeader>
										<TableHead>
											<TableRow>
												<TableCell sx={{ py: 0.5 }}>
													<Typography variant="overline" color="text.secondary">
														Name
													</Typography>
												</TableCell>
												<TableCell sx={{ py: 0.5 }}>
													<Typography variant="overline" color="text.secondary">
														Description
													</Typography>
												</TableCell>
												<TableCell align="right" sx={{ py: 0.5 }}>
													<Typography variant="overline" color="text.secondary">
														Action
													</Typography>
												</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{conversationTemplates.map((f: ConversationTemplate) => (
												<TableRow key={f.name}>
													<TableCell sx={{ py: 0.5 }}>
														<Typography variant="body2" sx={{ fontFamily: "monospace" }}>
															{f.name}
														</Typography>
													</TableCell>
													<TableCell sx={{ py: 0.5 }}>
														<Typography variant="body2" color="text.secondary">
															{f.description || ""}
														</Typography>
													</TableCell>
													<TableCell align="right" sx={{ py: 0.5 }}>
														<Stack direction="row" spacing={0.5} justifyContent="flex-end">
															<Tooltip title="Insert at cursor position">
																<IconButton
																	size="small"
																	onClick={(e) => {
																		e.stopPropagation();
																		handleInsertField(f.name, true);
																	}}
																>
																	<Plus size={16} />
																</IconButton>
															</Tooltip>
															<Tooltip title="Copy token">
																<IconButton
																	size="small"
																	onClick={(e) => {
																		e.stopPropagation();
																		navigator.clipboard.writeText(`${f.name}`);
																		toast.success("Copied to clipboard");
																	}}
																>
																	<CopySimple size={16} />
																</IconButton>
															</Tooltip>
														</Stack>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</Box>
							</Collapse>
						</Box>
					)}

					{summaryInfo && (
						<Box
							sx={{
								mt: 0,
								border: "1px solid var(--mui-palette-divider)",
								overflow: "hidden",
								maxHeight: "50%",
								overflowY: "auto",
								borderRadius: 0,
								borderBottomLeftRadius: "12px",
								borderBottomRightRadius: "12px",
							}}
						>
							<Stack
								direction="row"
								spacing={1}
								alignItems="center"
								justifyContent="space-between"
								sx={{
									px: 2,
									py: 1,
									cursor: "pointer",
								}}
								onClick={() => setIsSummaryOpen(!isSummaryOpen)}
							>
								<Stack
									direction={{ xs: "column", md: "row" }}
									spacing={1}
									alignItems={{ xs: "flex-start", md: "center" }}
								>
									<Typography variant="subtitle2">Summary Schema</Typography>
									<Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
										(No need to copy — it will be added to the prompt automatically.)
									</Typography>
								</Stack>
								<Stack direction="row" spacing={1} alignItems="center">
									<Tooltip title={"Copy to clipboard"}>
										<IconButton
											size="small"
											onClick={(e) => {
												e.stopPropagation();
												navigator.clipboard.writeText(JSON.stringify(summaryInfo, null, 2));
												toast.success("Copied to clipboard");
											}}
											sx={{ p: 0.5 }}
										>
											<CopyIcon size={16} />
										</IconButton>
									</Tooltip>
									<Tooltip title={isSummaryOpen ? "Collapse" : "Expand"}>
										<IconButton size="small" onClick={() => setIsSummaryOpen(!isSummaryOpen)} sx={{ p: 0.5 }}>
											{isSummaryOpen ? <CaretDownIcon size={16} /> : <CaretRightIcon size={16} />}
										</IconButton>
									</Tooltip>
								</Stack>
							</Stack>
							<Collapse in={isSummaryOpen} timeout={600}>
								<Card
									sx={{
										m: 2,
										mt: 0,
										p: 2,
										border: "1px solid var(--mui-palette-divider)",
										boxShadow: "none !important",
										backgroundColor:
											colorScheme === "dark" ? "var(--mui-palette-Skeleton-bg)" : "var(--mui-palette-grey-100)",
									}}
								>
									<Typography variant="body1" sx={{ whiteSpace: "pre-wrap", fontSize: 14 }}>
										{JSON.stringify(summaryInfo, null, 2)}
									</Typography>
								</Card>
							</Collapse>
						</Box>
					)}
				</DialogContent>
			</Dialog>
		</>
	);
}
